import { useState, useCallback } from 'react';

export interface FavouriteProduct {
  id: string | number;
  name: string;
  price: number;
  originalPrice: number;
  image: string;
  category: string;
  badge: string;
  sizes: string[];
}

const STORAGE_KEY = 'clothybazar_favourites';

export function getFavourites(): FavouriteProduct[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? (JSON.parse(saved) as FavouriteProduct[]) : [];
  } catch {
    return [];
  }
}

export function saveFavouritesToStorage(favourites: FavouriteProduct[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(favourites));
}

export function getFavouriteCount(): number {
  return getFavourites().length;
}

export function useFavourites() {
  const [favourites, setFavourites] = useState<FavouriteProduct[]>(() => getFavourites());

  const toggleFavourite = useCallback((product: FavouriteProduct) => {
    setFavourites(prev => {
      const exists = prev.some(f => f.id === product.id);
      const updated = exists
        ? prev.filter(f => f.id !== product.id)
        : [...prev, product];
      saveFavouritesToStorage(updated);
      return updated;
    });
  }, []);

  const removeFavourite = useCallback((id: string | number) => {
    setFavourites(prev => {
      const updated = prev.filter(f => String(f.id) !== String(id));
      saveFavouritesToStorage(updated);
      return updated;
    });
  }, []);

  const isFavourite = useCallback(
    (id: string | number) => favourites.some(f => String(f.id) === String(id)),
    [favourites]
  );

  return { favourites, toggleFavourite, removeFavourite, isFavourite };
}
